import { getGroupList } from '../api/getGroupList.js';
import { registerSession, redirectOtpAccess, redirectWizardOtp } from '../api/registerSession.js';
import { onRegisterSession } from './onRegisterSession.js';
import { validateLoginForm } from './validateLoginForm.js';
import { getLoginView, getLoginViewBtnSubmit, getLoginViewInputDomain, hideLoginError, showLoginError, setLoginSelectGroupVisible } from './getLoginView.js';
import { RESULT_HIDE_LOADER, RESULT_REDIRECT_DIRECTLY } from './constant.js';
import progressBar from '../../../shared/DOM/progressBar.js';
import { getTargetViewName } from '../../../shared/DOM/getTargetViewName.js';
import { isSamlAccess } from '../../../shared/DOM/isSamlAccess.js';
import { ACCESS_TOKEN, ID_TOKEN, TOKEN_TYPE, isAccountsAccess } from '../../../shared/DOM/isAccountsAccess.js';
import { isCryptAccess } from '../../../shared/DOM/isCryptAccess.js';
import { getOtpAccessBtnSubmit } from './otpAccess/getOtpAccessView.js';
import { getLinkFooter } from './getLinkFooter.js';
import { renderCertAccess } from './certAccess/renderCertAccess.js';
import { isCertAccess } from '../../../shared/DOM/isCertAccess.js';
import { redirectWithGet } from '../../../shared/api/redirect/redirect.js';

const WRONG_DOMAIN = 'login.error';

export async function renderForm() {
  const containerForm = document.getElementById('container-form');
  const loginParams = getLoginParams(containerForm);

  // Lo dejamos preparado por si saltamos a OTP
  // 12/2023 NUEVO: Solución remember user + pass > form que gestionamos en el onRegisterSession
  containerForm.insertAdjacentHTML('afterbegin', `<form id="login" action="/app" method="post"><ewc-form id="form-login" autofocus></ewc-form></form>`);

  // Intentamos submit
  if (launchLoginForm(loginParams)) {
    // Compatibilibidad web de clients: casos OTP
    if (loginParams.otpStep === 'showOTPAccessCode') {
      redirectOtpAccess(loginParams);
      return RESULT_HIDE_LOADER;
    } else if (loginParams.otpStep === 'showOTPConfigWizard') {
      redirectWizardOtp(loginParams);
      return RESULT_REDIRECT_DIRECTLY;
    }

    try {
      const tokena = await getLaunchLoginToken(loginParams);
      if (tokena) {
        prepareSpecialAccess(loginParams);
        await onRegisterSession(loginParams, tokena);
        return RESULT_REDIRECT_DIRECTLY; // acceso con token success, salimos
      }

      // else no token > navigateTo...
      return redirectLaunchLogin();
    } catch (e) {
      // validateSession
      if (e.message === 'enrollment') {
        return RESULT_REDIRECT_DIRECTLY;
      }

      // No ha ido bien el acceso desde el landing
      if (isAccountsAccess()) {
        redirectWithGet('/landing?exception=token_invalid');
        return RESULT_REDIRECT_DIRECTLY;
      } else if (isCryptAccess()) {
        redirectWithGet('/alert/error?icon=right-to-bracket&title=login.error_access&message=login.error');
        return RESULT_REDIRECT_DIRECTLY;
      }

      console.error('launchLoginForm', e);
    }
  }

  // O definitivamente nos quedamos en loginForm
  renderLoginForm(loginParams);
  return RESULT_HIDE_LOADER;
}

function getLoginParams(containerForm) {
  return {
    token: containerForm.getAttribute('token'),
    user: containerForm.getAttribute('user'),
    domain: containerForm.getAttribute('domain'),
    group: containerForm.getAttribute('group'),
    password: containerForm.getAttribute('pwd'),
    otpStep: containerForm.getAttribute('otpStep'),
    urlSignup: containerForm.getAttribute('urlSignup'),
    urlCert: containerForm.getAttribute('urlCert'),
  };
}

function prepareSpecialAccess(loginParams) {
  if (isSamlAccess()) {
    loginParams.samlAccess = true; // change clientType
  } else if (isAccountsAccess()) {
    const urlParams = new URLSearchParams(window.location.search);
    loginParams.token_type = urlParams.get(TOKEN_TYPE); // set accounts
    sessionStorage.setItem(ID_TOKEN, urlParams.get(ID_TOKEN) || '');
    sessionStorage.setItem(ACCESS_TOKEN, urlParams.get('token') || '');
  } else if (isCertAccess()) {
    loginParams.certAccess = true; // para validateSession
  }
}

// accessToken || accessDataComplete
function launchLoginForm(login) {
  return login.domain && (login.token || launchWithoutToken(login));
}

// Movemos user (menos restrictivos)
function launchWithoutToken(login) {
  return login.user && login.group && login.password;
}

// 10/2023: Cubrimos el único caso que va sin redirect > showOTPAccessCode
function redirectLaunchLogin() {
  return getOtpAccessBtnSubmit() ? RESULT_HIDE_LOADER : RESULT_REDIRECT_DIRECTLY;
}

async function getLaunchLoginToken(loginParams) {
  if (loginParams.token) {
    return loginParams.token;
  }

  return registerSession(loginParams);
}

function renderLoginForm(loginParams) {
  const eForm = createLoginForm(loginParams);
  const eBtnSubmit = getLoginViewBtnSubmit();

  initLoginForm(eForm, loginParams, submitLoginForm);

  /////////////////////////////

  // eBtnSubmit
  async function submitLoginForm() {
    // Bloqueamos peticiones simultáneas (intro)
    if (eBtnSubmit.state === 'loading') {
      return;
    }

    try {
      eBtnSubmit.state = 'loading';
      progressBar.loading();

      let loginForm = getLoginForm(eForm);

      // eForm.valid personalizado
      const valid = validateLoginForm(loginForm);

      // Exit error
      if (!valid) {
        eBtnSubmit.state = 'rejected';
        progressBar.rejected();
        return;
      }

      // PARTE I: El grupo por defecto no aparece porque se puede consultar
      if (!loginForm.group) {
        const groupArray = await getGroupList({ domain: loginForm.domain });
        onGetGroupList(eForm, groupArray);

        // Consulta sobre el scope (loginForm no actualizado)
        if (!eForm.getValueFromScope('group')) {
          // Estamos pendientes del selectGroup
          throw Error('login.group_error');
        }

        // Update values (clone)
        loginForm = eForm.value;
      }

      // PARTE II: ValidateLoginForm & grupo = registerSession
      const tokena = await registerSession(loginForm);
      if (tokena) {
        await onRegisterSession(loginForm, tokena, document.forms['login']);
      }
      // else no token + no error > navigateTo (enrollment, changePwd, ...)

      // Finalizamos correctamente por si navegan hacia atrás en el navegador:
      progressBar.resolved();
      // Con retardo para visualizar solo el check de ok
      setTimeout(() => {
        eBtnSubmit.state = 'resolved';
      }, 1000);
    } catch (e) {
      eBtnSubmit.state = 'rejected';
      progressBar.rejected();
      showLoginError(e.message);
    }
  }
}

function createLoginForm({ user, domain, group, urlSignup }) {
  const eForm = document.getElementById('form-login');
  eForm.render({
    value: {
      user,
      password: '',
      domain,
      group,
      urlSignup,
    },
    view: getLoginView(eForm),
  });

  createLoginFooter(urlSignup);

  // Migración E7: equivale a loginCtrl#activate
  hideLoginError();
  setLoginSelectGroupVisible(false);

  return eForm;
}

function getLoginForm(eForm) {
  const loginForm = eForm.value;

  // Si el dominio está en blanco, copiamos el usuario
  if (!loginForm.domain) {
    loginForm.domain = loginForm.user;
    eForm.setValueToScope('domain', loginForm.domain);
  }

  return loginForm;
}

function createLoginFooter(urlSignup) {
  const contentSignup = urlSignup ? getLinkFooter('linkRegisterUser', 'login.singup') : '';
  document.getElementById('container-footer').insertAdjacentHTML('afterbegin', `${getLinkFooter('linkRecoverPass', 'login.recover_password')}${contentSignup}`);
}

function initLoginForm(eForm, loginParams, submitLoginForm) {
  eForm.addEventListener('keyup', (event) => {
    if (event.key === 'Enter' && !!getLoginViewBtnSubmit()) {
      submitLoginForm();
    }
  });

  document.body.addEventListener('click', (event) => {
    const target = getTargetViewName(event);

    if (target === 'btnSubmit') {
      submitLoginForm();
    } else if (target === 'linkRecoverPass') {
      redirectRecoverPwd(eForm.value);
    } else if (target === 'linkRegisterUser') {
      redirectRegisterUser(eForm.value);
    } else if (target === 'btnCertAccess') {
      renderCertAccess(loginParams, eForm.value);
    }
  });

  onChangeInputDomain(eForm);
}

function onChangeInputDomain(eForm) {
  // Opcional
  const inputDomain = getLoginViewInputDomain();
  inputDomain?.addEventListener('change', () => {
    setLoginSelectGroupVisible(false);
    eForm.setValueToScope('groupArray', []);
    eForm.setValueToScope('group', '');
  });
}

function onGetGroupList(eForm, groupArray) {
  if (!groupArray?.length) {
    throw new Error(WRONG_DOMAIN);
  }

  eForm.setValueToScope('groupArray', groupArray);
  const selectVisible = groupArray.length > 1;
  setLoginSelectGroupVisible(selectVisible);

  if (!selectVisible) {
    eForm.setValueToScope('group', groupArray[0]);
  }
}

/* LINKS */

function redirectRecoverPwd(loginForm) {
  const urlParams = new URLSearchParams(window.location.search);

  // override: Manda el formulario
  urlParams.set('user', loginForm.user);
  urlParams.set('domain', loginForm.domain);
  urlParams.set('group', loginForm.group);

  redirectWithGet(`/recoverPwd?${urlParams}`);
}

function redirectRegisterUser(loginForm) {
  redirectWithGet(loginForm.urlSignup);
}
